/**
 * Modal Component
 * Module 07 - Dashboard UI
 * Common Component
 * 
 * Reusable modal dialog with backdrop and animations
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  children: React.ReactNode;
  showCloseButton?: boolean;
}

/**
 * Modal component with backdrop and animations
 * 
 * @param open - Whether modal is open
 * @param onClose - Callback when modal should close
 * @param title - Optional modal title
 * @param size - Modal size (small/medium/large)
 * @param showCloseButton - Show X button in header (default: true)
 * @param children - Modal content
 * 
 * @example
 * <Modal open={isOpen} onClose={handleClose} title="Upload Model">
 *   <p>Modal content</p>
 * </Modal>
 */
const Modal: React.FC<ModalProps> = ({ 
  open, 
  onClose, 
  title, 
  size = 'medium',
  showCloseButton = true,
  children 
}) => {
  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-lg',
    large: 'max-w-4xl',
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className={cn('relative z-50', sizeClasses[size])}
        onClose={onClose}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                className={cn(
                  'w-full transform overflow-hidden rounded-2xl text-left align-middle transition-all',
                  'bg-white/95 backdrop-blur-xl',
                  'border border-white/20 shadow-2xl',
                  'ring-1 ring-black/5',
                  sizeClasses[size]
                )}
              >
                {/* Header */}
                {(title || showCloseButton) && (
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/50 bg-gradient-to-r from-white/50 to-transparent">
                    {title && (
                      <Dialog.Title 
                        as="h3" 
                        className="text-lg font-semibold text-gray-900"
                      >
                        {title}
                      </Dialog.Title>
                    )}
                    
                    {showCloseButton && (
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={onClose}
                        aria-label="Close"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Content */}
                <div className="px-6 py-4">
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
